import { useCallback, useEffect, useState } from 'react'
import { AccountSiteBUDto } from '../interfaces'
import { getAccountSiteBUByAccountSiteID } from '../services/AccountServices'

/**
 * Trae las unidades de negocio del contrato (AccountSite) del cliente activo.
 *
 * Vive en un hook y no en el store persistido a propósito: al estar cacheado
 * por accountSiteID, cuatro guardas que el slice necesitaba escritas a mano
 * dejan de existir como concepto (descartar respuestas viejas tras cambiar de
 * cliente, limpiar en logout, leer defensivo porque autoMergeLevel1 reemplaza
 * el slice, y saltar cuando no hay contrato). Cambiar de cliente cambia la
 * clave, y ya.
 *
 * Costo conocido: la caché no tiene TTL, así que un cambio del contrato en
 * backoffice se ve recién al recargar la página.
 */

export type ContractDestinationStatus = 'loading' | 'ok' | 'failed'

export interface ContractDestinationBUs {
  /** undefined = todavía no sabemos. [] = sabemos que no hay filas. */
  rows: AccountSiteBUDto[] | undefined
  status: ContractDestinationStatus
  /** Reintenta tras un fallo. No hace nada si no hay contrato. */
  retry: () => void
}

const cache = new Map<number, AccountSiteBUDto[]>()

/** Sólo para pruebas: la caché es de por vida del proceso. */
export const clearContractDestinationBUsCache = (): void => {
  cache.clear()
}

const hasContract = (accountSiteID?: number | null): accountSiteID is number =>
  typeof accountSiteID === 'number' &&
  Number.isFinite(accountSiteID) &&
  accountSiteID > 0

interface HookState {
  rows: AccountSiteBUDto[] | undefined
  status: ContractDestinationStatus
}

const initialState = (accountSiteID?: number | null): HookState => {
  // Sin contrato no se consulta NUNCA. El endpoint responde 400 cuando
  // AccountSiteID es 0 (AccountController.cs:1398-1403); sin esta guarda todo
  // cliente de taquilla caería en 'failed' y se quedaría con el campo de
  // destino bloqueado, que es el camino más común de la aplicación.
  if (!hasContract(accountSiteID)) return { rows: [], status: 'ok' }

  const cached = cache.get(accountSiteID)
  if (cached) return { rows: cached, status: 'ok' }

  return { rows: undefined, status: 'loading' }
}

export const useContractDestinationBUs = (
  accountSiteID?: number | null
): ContractDestinationBUs => {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<HookState>(() =>
    initialState(accountSiteID)
  )

  useEffect(() => {
    if (!hasContract(accountSiteID)) {
      setState({ rows: [], status: 'ok' })
      return
    }

    const cached = cache.get(accountSiteID)
    if (cached) {
      setState({ rows: cached, status: 'ok' })
      return
    }

    let cancelled = false
    setState({ rows: undefined, status: 'loading' })

    getAccountSiteBUByAccountSiteID(accountSiteID).then((response) => {
      if (cancelled) return

      if (response.didError || !response.model) {
        setState({ rows: undefined, status: 'failed' })
        return
      }

      cache.set(accountSiteID, response.model)
      setState({ rows: response.model, status: 'ok' })
    })

    return () => {
      cancelled = true
    }
  }, [accountSiteID, attempt])

  const retry = useCallback(() => {
    setAttempt((previous) => previous + 1)
  }, [])

  return { rows: state.rows, status: state.status, retry }
}
